import { inject, Injectable } from '@angular/core';
import { WindowRef } from '@spartacus/core';
import { StorageService } from 'src/app/service/storage/storage.service';
import { ASM_ENABLED_LOCAL_STORAGE_KEY } from 'src/app/spartacus/features/asm/asm-constant';

@Injectable({ providedIn: 'root' })
export class AsmModeService {
  private storageService = inject(StorageService);
  private winRef = inject(WindowRef);

  /**
   * Single source of truth for ASM mode detection.
   * Checks URL param `?asm=true` first, then falls back to localStorage flag.
   * SSR-safe: returns false on the server since ASM is browser-only.
   */
  isAsmMode(): boolean {
    // Guard: SSR has no window/localStorage
    if (!this.winRef.isBrowser()) {
      return false;
    }

    const urlParams = new URLSearchParams(this.winRef.nativeWindow!.location.search);
    const asmParam = urlParams.get('asm');

    if (asmParam === 'true') {
      this.enableAsmMode();
      return true;
    }

    if (asmParam === 'false') {
      this.disableAsmMode();
      return false;
    }

    return this.storageService.getLocalStorageItem(ASM_ENABLED_LOCAL_STORAGE_KEY) === 'true';
  }

  checkSwitchToNonASM(): boolean {
    // Guard: SSR has no window/localStorage
    if (!this.winRef.isBrowser()) {
      return false;
    }
    const urlParams = new URLSearchParams(this.winRef.nativeWindow!.location.search);
    const asmParam = urlParams.get('asm');

    // Read the persisted ASM flag directly (don't call isAsmMode() to avoid recursion/side effects)
    const wasAsmEnabled = this.storageService.getSessionStorageItem('asmLoginTriggered') === 'true';

    const isSwitchingToNonAsm = asmParam === 'false' && wasAsmEnabled;

    if (isSwitchingToNonAsm) {
      return true;
    }

    return false;
  }

  enableAsmMode(): void {
    if (!this.winRef.isBrowser()) return;
    this.storageService.setLocalStorageitem(ASM_ENABLED_LOCAL_STORAGE_KEY, 'true');
  }

  disableAsmMode(): void {
    if (!this.winRef.isBrowser()) return;
    this.storageService.removeLocalStorageItem(ASM_ENABLED_LOCAL_STORAGE_KEY);
  }
}
